import { useState, useEffect, useRef } from 'react';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCE_QUERY } from '../utils/cadence-queries';

/**
 * Fetch a single cadence by ID with its events and edges.
 * Returns { cadence, loading, error, refetch }.
 */
const useCadence = (cadenceId) => {
  const [cadence, setCadence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!cadenceId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Only show full-page loading on initial load — keep stale data visible during refetch
    if (!hasLoadedRef.current) {
      setLoading(true);
      setCadence(null);
    }
    setError(null);

    cadenceGqlCall(CADENCE_QUERY, { id: String(cadenceId) })
      .then((data) => {
        if (!cancelled) {
          const c = data.cadence;
          setCadence(c ? { ...c, cadenceType: c.cadenceType?.toLowerCase() || c.cadenceType } : c);
          if (c) hasLoadedRef.current = true;
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to fetch cadence');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cadenceId, fetchKey]);

  const refetch = () => setFetchKey((k) => k + 1);

  return { cadence, loading, error, refetch };
};

export default useCadence;
